<!-- Toast-lite -->
<div class="debug-toast" *ngIf="toastMessage() as msg" [class.error]="msg.error">
  {{ msg.text }}
</div>

<div class="xbrl-files-debug-root">
  <!-- ─── Debug banner ─────────────────────────────────────────── -->
  <div class="debug-banner">
    <div class="debug-banner-row">
      <div class="debug-banner-left">
        <span class="debug-badge">DEBUG</span>
        <strong>XBRL Files — Developer Layout</strong>
      </div>
      <div class="debug-banner-right">
        <button type="button"
                class="debug-btn primary"
                (click)="reloadAll()"
                [disabled]="!!bulkLoadProgress()">
          <ng-container *ngIf="bulkLoadProgress() as p; else bulkLabel">
            Loading {{ p.loaded }}/{{ p.total }}...
          </ng-container>
          <ng-template #bulkLabel>Reload filing info</ng-template>
        </button>
      </div>
    </div>

    <div class="debug-banner-row">
      <div class="debug-hint">
        <kbd>Ctrl</kbd>+<kbd>Alt</kbd>+<kbd>D</kbd> toggle &middot;
        Expand a row to lazy-load its filing info &middot;
        Filters apply client-side to the current page
      </div>
      <ng-container *ngIf="summary() as summary">
        <span class="stats-pill">
          <span class="stat stat-in-progress">{{ summary.inProgress }} in progress</span>
          <span class="stat stat-review">{{ summary.review }} review</span>
          <span class="stat stat-success">{{ summary.success }} success</span>
          <span class="stat stat-failed">{{ summary.failed }} failed</span>
        </span>
      </ng-container>
    </div>

    <!-- ─── Filing-info filters ────────────────────────────────── -->
    <div class="filter-section">
      <div class="filter-grid">
        <div class="debug-filter-group">
          <label class="debug-filter-label">Conversion Status</label>
          <p-select [options]="statusOptions"
                    [ngModel]="statusFilter()"
                    (ngModelChange)="statusFilter.set($event)"
                    optionLabel="label" optionValue="value"
                    placeholder="All"
                    styleClass="debug-filter-select" />
        </div>
        <div class="debug-filter-group">
          <label class="debug-filter-label">Nature of Financial Statements</label>
          <p-select [options]="natureOptions()"
                    [ngModel]="natureFilter()"
                    (ngModelChange)="natureFilter.set($event)"
                    optionLabel="label" optionValue="value"
                    placeholder="All"
                    styleClass="debug-filter-select" />
        </div>
        <div class="debug-filter-group">
          <label class="debug-filter-label">Status of Company</label>
          <p-select [options]="companyStatusOptions()"
                    [ngModel]="companyStatusFilter()"
                    (ngModelChange)="companyStatusFilter.set($event)"
                    optionLabel="label" optionValue="value"
                    placeholder="All"
                    styleClass="debug-filter-select" />
        </div>
        <div class="debug-filter-group">
          <label class="debug-filter-label">Accounting Standards</label>
          <p-select [options]="accountingStdOptions()"
                    [ngModel]="accountingStdFilter()"
                    (ngModelChange)="accountingStdFilter.set($event)"
                    optionLabel="label" optionValue="value"
                    placeholder="All"
                    styleClass="debug-filter-select" />
        </div>
        <div class="debug-filter-group">
          <label class="debug-filter-label">Audit Status</label>
          <p-select [options]="auditStatusOptions()"
                    [ngModel]="auditStatusFilter()"
                    (ngModelChange)="auditStatusFilter.set($event)"
                    optionLabel="label" optionValue="value"
                    placeholder="All"
                    styleClass="debug-filter-select" />
        </div>
        <div class="debug-filter-group">
          <label class="debug-filter-label">Type of Submission</label>
          <p-select [options]="submissionTypeOptions()"
                    [ngModel]="submissionTypeFilter()"
                    (ngModelChange)="submissionTypeFilter.set($event)"
                    optionLabel="label" optionValue="value"
                    placeholder="All"
                    styleClass="debug-filter-select" />
        </div>
      </div>

      <div class="filter-utilities">
        <div class="debug-filter-group filter-search-group">
          <label class="debug-filter-label">Search</label>
          <input type="text" pInputText
                 class="debug-search-input"
                 placeholder="Filename or Client Company"
                 [ngModel]="companyFileSearchQuery()"
                 (ngModelChange)="companyFileSearchQuery.set($event)" />
        </div>
        <span class="clear-link" *ngIf="hasActiveDebugFilters()" (click)="clearDebugFilters()">
          Clear debug filters
        </span>
      </div>
    </div>
  </div>

  <!-- ─── Loading skeleton (from host) ────────────────────────── -->
  @if (host.isTableLoading()) {
    <div class="mx-4 md:mx-[24px]">
      <p-skeleton width="100%" height="400px"></p-skeleton>
    </div>
  } @else {
    <!-- ─── Enhanced table ───────────────────────────────────────── -->
    <div class="debug-table-wrapper">
      <p-table [value]="filteredConversions()"
               [paginator]="false"
               [scrollable]="true"
               dataKey="id"
               styleClass="debug-table">
        <ng-template pTemplate="header">
          <tr>
            <th class="col-expand"></th>
            <th class="col-no">No.</th>
            <th class="col-filename">Filename</th>
            <th class="col-company">Client Company</th>
            <th class="col-status">Conversion Status</th>
            <th class="col-nature">Nature of FS</th>
            <th class="col-company-status">Status of Company</th>
            <th class="col-acct">Acct. Standards</th>
            <th class="col-audit">Audit Status</th>
            <th class="col-submission">Type of Submission</th>
            <th class="col-date">Created Date</th>
            <th class="col-created-by">Created By</th>
            <th class="col-actions">Actions</th>
          </tr>
        </ng-template>

        <ng-template pTemplate="body" let-item let-rowIndex="rowIndex">
          <tr [class.row-expanded]="isExpanded(item.id)"
              [class.row-in-progress]="item.status === MbrsStatusEnum.InProgress">
            <!-- Expand toggle -->
            <td class="col-expand">
              @if (item.status !== MbrsStatusEnum.InProgress) {
                <button type="button"
                        class="expand-btn"
                        (click)="toggleRow(item.id)"
                        [title]="isExpanded(item.id) ? 'Collapse' : 'Expand to load filing info'">
                  <span [class.chevron-open]="isExpanded(item.id)">&#9656;</span>
                </button>
              }
            </td>

            <!-- No. -->
            <td class="col-no">{{ host.first() + rowIndex + 1 }}</td>

            <!-- Filename -->
            <td class="col-filename">
              <span [pTooltip]="item.fileName" tooltipPosition="top">
                {{ item.fileName | truncate : 35 }}
              </span>
            </td>

            <!-- Company -->
            <td class="col-company">
              @if (item.company) {
                <!-- Real anchor: routerLink renders an href so right-click
                     "Open in New Tab", middle-click and Ctrl/Cmd+click work
                     natively. Null routerLink (InProgress) renders no href;
                     .disabled keeps pointer-events:none as the second guard. -->
                <a class="company-link"
                   [class.disabled]="item.status === MbrsStatusEnum.InProgress"
                   [routerLink]="item.status === MbrsStatusEnum.InProgress ? null : ['/mbrs/filing']"
                   [queryParams]="{ id: item.id, companyName: item.company, fileName: item.fileName }">
                  {{ item.company | titlecase }}
                </a>
              } @else {
                <span class="muted">-</span>
              }
            </td>

            <!-- Conversion Status -->
            <td class="col-status">
              @if (item.status === MbrsStatusEnum.PendingValidation) {
                <!-- AS-842: skeleton while the frontend silently validates. -->
                <p-skeleton width="100px" height="24px" />
              } @else {
                <div [ngClass]="host.conversionListingService.getStatusClass(item.status)"
                     class="status-badge">
                  {{ host.conversionListingService.getDisplayBaseStatus(item.status)
                  }}@if (host.conversionListingService.hasWarningAsterisk(item.status)) {<span
                      class="!text-red-500"
                      >*</span
                    >}
                </div>
              }
            </td>

            <!-- Nature of Financial Statements -->
            <td class="col-nature">
              @if (hasFilingInfo(item.id)) {
                <span class="info-chip">{{ getFilingInfoField(item.id, DEI_EE_MFRS.NatureOfFinancialStatements) || '-' }}</span>
              } @else if (isLoading(item.id)) {
                <p-skeleton width="80px" height="20px" />
              } @else {
                <span class="muted unloaded-hint">--</span>
              }
            </td>

            <!-- Status of Company -->
            <td class="col-company-status">
              @if (hasFilingInfo(item.id)) {
                <span class="info-chip">{{ getFilingInfoField(item.id, DEI.StatusOfCompany) || '-' }}</span>
              } @else if (isLoading(item.id)) {
                <p-skeleton width="80px" height="20px" />
              } @else {
                <span class="muted unloaded-hint">--</span>
              }
            </td>

            <!-- Accounting Standards -->
            <td class="col-acct">
              @if (hasFilingInfo(item.id)) {
                <span class="info-chip" [pTooltip]="getFilingInfoField(item.id, DEI.BasisOfAccountingStandardsAppliedToPrepareFinancialStatements)" tooltipPosition="top">
                  {{ getFilingInfoField(item.id, DEI.BasisOfAccountingStandardsAppliedToPrepareFinancialStatements) || '-' }}
                </span>
              } @else if (isLoading(item.id)) {
                <p-skeleton width="80px" height="20px" />
              } @else {
                <span class="muted unloaded-hint">--</span>
              }
            </td>

            <!-- Audit Status -->
            <td class="col-audit">
              @if (hasFilingInfo(item.id)) {
                <span class="info-chip">{{ getFilingInfoField(item.id, DEI.DisclosureOfFinancialStatementsAuditStatus) || '-' }}</span>
              } @else if (isLoading(item.id)) {
                <p-skeleton width="80px" height="20px" />
              } @else {
                <span class="muted unloaded-hint">--</span>
              }
            </td>

            <!-- Type of Submission -->
            <td class="col-submission">
              @if (hasFilingInfo(item.id)) {
                <span class="info-chip">{{ getFilingInfoField(item.id, DEI.TypeOfSubmission) || '-' }}</span>
              } @else if (isLoading(item.id)) {
                <p-skeleton width="80px" height="20px" />
              } @else {
                <span class="muted unloaded-hint">--</span>
              }
            </td>

            <!-- Created Date -->
            <td class="col-date">
              <div>{{ item.conversionDate | date : 'dd MMM yyyy' }}</div>
              <div class="date-time">{{ item.conversionDate | date : 'shortTime' }}</div>
            </td>

            <!-- Created By (a user id, not a name, until the backend supplies one) -->
            <td class="col-created-by">{{ item.createdBy || '-' }}</td>

            <!-- Actions -->
            <td class="col-actions">
              @if (item.status !== MbrsStatusEnum.InProgress) {
                <button type="button" class="action-btn" (click)="openInspect(item.id)" title="Inspect full filing info JSON">
                  &#128269; Inspect
                </button>
                <button type="button" class="action-btn" (click)="host.downloadXbrlFile(item)"
                        [disabled]="item.status !== MbrsStatusEnum.Success" title="Download XBRL">
                  &#11015; Download
                </button>
                <button type="button" class="action-btn action-delete" (click)="host.deleteXbrlFile(item)" title="Delete">
                  &#128465; Delete
                </button>
              }
            </td>
          </tr>

          <!-- Expanded row: filing info detail -->
          @if (isExpanded(item.id)) {
            <tr class="expansion-row">
              <td [attr.colspan]="12">
                <div class="expansion-content">
                  @if (isLoading(item.id)) {
                    <div class="expansion-loading">
                      <p-skeleton width="100%" height="120px" />
                    </div>
                  } @else if (hasFilingInfo(item.id)) {
                    <div class="filing-info-grid">
                      @for (field of filingInfoFields; track field.qname) {
                        <div class="filing-info-item">
                          <span class="filing-info-label">{{ field.label }}</span>
                          <span class="filing-info-value">
                            {{ getFilingInfoField(item.id, field.qname) || '(empty)' }}
                          </span>
                        </div>
                      }
                    </div>
                    <div class="expansion-actions">
                      <button type="button" class="debug-btn" (click)="openInspect(item.id)">
                        View full JSON
                      </button>
                    </div>
                  } @else {
                    <div class="expansion-empty">
                      <button type="button" class="debug-btn primary" (click)="loadFilingInfo(item.id)">
                        Load Filing Info
                      </button>
                    </div>
                  }
                </div>
              </td>
            </tr>
          }
        </ng-template>

        <ng-template pTemplate="emptymessage">
          <tr>
            <td [attr.colspan]="12" class="empty-message">
              @if (hasActiveDebugFilters()) {
                No conversions match the current debug filters.
                <button type="button" class="debug-btn" (click)="clearDebugFilters()">Clear filters</button>
              } @else {
                No data available.
              }
            </td>
          </tr>
        </ng-template>
      </p-table>
    </div>

    <!-- ─── Footer with paginator ──────────────────────────────── -->
    <div class="debug-footer">
      <span class="footer-info">
        Showing
        <strong>{{ host.first() + 1 }} - {{ host.min(host.first() + host.rows(), host.totalRecords()) }}</strong>
        of <strong>{{ host.totalRecords() }}</strong> items
        @if (hasActiveDebugFilters()) {
          ({{ filteredConversions().length }} after debug filters)
        }
      </span>
      <p-paginator
        (onPageChange)="host.onPageChange($event)"
        [first]="host.first()"
        [rows]="host.rows()"
        [totalRecords]="host.totalRecords()"
        [pageLinkSize]="3"
        [showFirstLastIcon]="true"
        styleClass="debug-paginator" />
      <span class="footer-hint">
        Debug filters are client-side only. Pagination is server-side.
      </span>
    </div>
  }

  <!-- ─── Inspect panel (slide-out) ──────────────────────────────── -->
  @if (inspectedConversionId()) {
    <div class="inspect-backdrop" (click)="closeInspect()"></div>
    <aside class="inspect-panel">
      <div class="inspect-header">
        <strong>Filing Info — {{ inspectedConversionId() }}</strong>
        <div class="inspect-header-actions">
          <button type="button" class="debug-btn" (click)="copyInspectJson()">Copy JSON</button>
          <button type="button" class="inspect-close" (click)="closeInspect()">&times;</button>
        </div>
      </div>
      <pre class="inspect-json">{{ inspectedJson() }}</pre>
    </aside>
  }
</div>
